/**
 * Short-lived side channel that records the last block a user physically
 * interacted with. Block wrappers stamp `{ blockId, layoutId }` into this ref
 * on pointerdown (capture phase). Global action dispatchers (the patched
 * `client.actions.*` and `window.Tapcart.actions.*`) peek at it to enrich
 * action payloads that were built outside the block-scoped wrappers (e.g.
 * blocks that call `window.Tapcart.actions.addToCart(...)` directly or fire
 * raw `window.postMessage` into the legacy webbridge).
 *
 * Only used as a fallback: if the payload already has `_analytics.blockId` or
 * a flat `blockId`, the stamp is ignored. TTL keeps stale attribution from
 * leaking to actions that weren't user-triggered (timers, auto-refreshes).
 */

const TTL_MS = 1500

type BlockAttribution = { blockId: string; layoutId?: string }

let pending: (BlockAttribution & { timestamp: number }) | null = null

/**
 * Module-level flag mirroring the block-analytics LD flag value.
 * Set by clientLayoutWrapper (and deprecatedClientLayoutWrapper) during render,
 * before any useEffects fire, so webbridge.tsx can gate patchActionsForBlockAttribution
 * without needing the flag threaded through the root layout provider tree.
 */
let _blockAnalyticsEnabled = false
export const setBlockAnalyticsEnabled = (enabled: boolean): void => {
  _blockAnalyticsEnabled = enabled
}
export const isBlockAnalyticsEnabled = (): boolean => _blockAnalyticsEnabled

export const setPendingBlockAttribution = (
  attribution: BlockAttribution
): void => {
  if (!attribution?.blockId) return
  pending = { ...attribution, timestamp: Date.now() }
}

export const consumePendingBlockAttribution = ():
  | BlockAttribution
  | undefined => {
  if (!pending) {
    // No pending attribution — action was triggered without a preceding
    // pointerdown (e.g. programmatic, timer, auto-refresh).
    // eslint-disable-next-line no-console
    console.debug("[block-attribution] consume: no pending attribution")
    return undefined
  }
  if (Date.now() - pending.timestamp > TTL_MS) {
    // Attribution expired — action fired more than TTL_MS after pointerdown
    // (e.g. debounced cart operation, delayed bridge call).
    // eslint-disable-next-line no-console
    console.debug("[block-attribution] consume: attribution expired", {
      blockId: pending.blockId,
      ageMs: Date.now() - pending.timestamp,
    })
    pending = null
    return undefined
  }
  const result = { blockId: pending.blockId, layoutId: pending.layoutId }
  pending = null
  return result
}
